import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FormField } from "@/components/ui/FormField";
import { useForgotPassword, useResetPassword } from "@/features/auth/useAuth";
import { getApiErrorMessage } from "@/api/client";
import { toast } from "@/store/toast.store";

const emailSchema = z.object({ email: z.string().email("Enter a valid email") });

const resetSchema = z
  .object({
    code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code"),
    newPassword: z.string().min(8, "At least 8 characters").max(128),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match",
  });

type EmailValues = z.infer<typeof emailSchema>;
type ResetValues = z.infer<typeof resetSchema>;

/** Two steps on one page: request a code by email, then enter it with a new password. */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const forgot = useForgotPassword();
  const reset = useResetPassword();
  const [email, setEmail] = useState<string | null>(null);

  const emailForm = useForm<EmailValues>({ resolver: zodResolver(emailSchema) });
  const resetForm = useForm<ResetValues>({ resolver: zodResolver(resetSchema) });

  const sendCode = (address: string) =>
    forgot.mutate(address, {
      onSuccess: () => {
        setEmail(address);
        toast.success("If that email belongs to an admin, a code is on its way");
      },
    });

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-4">
      <div className="w-full max-w-sm card p-8">
        <h1 className="text-lg font-semibold text-ink">Reset your password</h1>
        <p className="mb-6 mt-1 text-sm text-ink-muted">
          {email
            ? `Enter the 6-digit code sent to ${email} and choose a new password.`
            : "Enter your admin email and we will send you a reset code."}
        </p>

        {!email ? (
          <form className="flex flex-col gap-4" onSubmit={emailForm.handleSubmit((v) => sendCode(v.email))}>
            <FormField label="Email" htmlFor="email" error={emailForm.formState.errors.email?.message}>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                placeholder="you@eyyauto.in"
                error={!!emailForm.formState.errors.email}
                {...emailForm.register("email")}
              />
            </FormField>
            {forgot.isError && <p className="text-sm text-danger-500">{getApiErrorMessage(forgot.error)}</p>}
            <Button type="submit" variant="primary" size="lg" loading={forgot.isPending}>
              Send reset code
            </Button>
          </form>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={resetForm.handleSubmit((v) =>
              reset.mutate(
                { email, code: v.code, newPassword: v.newPassword },
                {
                  onSuccess: () => {
                    toast.success("Password reset. Please sign in.");
                    navigate("/login", { replace: true });
                  },
                },
              ),
            )}
          >
            <FormField label="Reset code" htmlFor="code" error={resetForm.formState.errors.code?.message}>
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                error={!!resetForm.formState.errors.code}
                {...resetForm.register("code")}
              />
            </FormField>
            <FormField label="New password" htmlFor="newPassword" error={resetForm.formState.errors.newPassword?.message}>
              <Input
                id="newPassword"
                type="password"
                autoComplete="new-password"
                error={!!resetForm.formState.errors.newPassword}
                {...resetForm.register("newPassword")}
              />
            </FormField>
            <FormField
              label="Confirm new password"
              htmlFor="confirmPassword"
              error={resetForm.formState.errors.confirmPassword?.message}
            >
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                error={!!resetForm.formState.errors.confirmPassword}
                {...resetForm.register("confirmPassword")}
              />
            </FormField>
            {reset.isError && <p className="text-sm text-danger-500">{getApiErrorMessage(reset.error)}</p>}
            <Button type="submit" variant="primary" size="lg" loading={reset.isPending}>
              Reset password
            </Button>
            <div className="flex justify-between text-sm">
              <button
                type="button"
                className="text-teal-600 hover:underline disabled:opacity-50"
                disabled={forgot.isPending}
                onClick={() => sendCode(email)}
              >
                Resend code
              </button>
              <button type="button" className="text-ink-muted hover:underline" onClick={() => setEmail(null)}>
                Change email
              </button>
            </div>
            {forgot.isError && <p className="text-sm text-danger-500">{getApiErrorMessage(forgot.error)}</p>}
          </form>
        )}

        <Link to="/login" className="mt-6 block text-center text-sm text-teal-600 hover:underline">
          Back to sign in
        </Link>
      </div>
    </div>
  );
}
